import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import IconBox from "components/icons/IconBox";
import { IconArrowBack } from "components/icons/Icons";

import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { uploadProjectSelector } from "store/customerProject/selector";
import { userSelector } from "store/user/selector";
import { ServiceRowObj } from "../../variables/serviceData";
import PaymentStep from "../PaymentStep";
import { ProjectSelectTable } from "./ProjectSelectTable";
import { ServiceCard } from "../ServiceCard";
import { useEligibleProjectSelection } from "./useEligibleProjectSelection";
import { CardInfo, Order, ServiceProcessStep } from "./types";
import { useServicePayment } from "./hooks/useServicePayment";

export default function ServiceItem(props: {
  allActiveProjects: Order[];
  item: ServiceRowObj;
  onRefresh?: Function;
  [x: string]: any;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalSize = useBreakpointValue({ base: "full", md: "4xl" }) ?? "4xl";
  const isMobile = (useBreakpointValue({ base: true, md: false }) ?? false) === true;
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const { item, onRefresh, allActiveProjects, ...rest } = props;
  const [cardInfo, setCardInfo] = useState<CardInfo | null>(null);
  const [processStep, setProcessStep] = useState<ServiceProcessStep>(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [shippingRecipient, setShippingRecipient] = useState("");
  const [shippingStreet, setShippingStreet] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");
  const [isShippingValid, setIsShippingValid] = useState(true);
  const perPage = 10;
  const uploadProject = useSelector(uploadProjectSelector);

  const {
    selectedOrder,
    setSelectedOrder,
    currentPage,
    setCurrentPage,
    eligibleOrders,
    totalPages,
    pagedOrders,
    orderCountForGroup,
  } = useEligibleProjectSelection({
    orders: allActiveProjects,
    serviceGroup: item.group,
    serviceKey: item.key,
    perPage,
  });

  const onStartPaymentStep = useCallback(() => {
    setProcessStep(0);
    setCardInfo(null);
    setShippingRecipient("");
    setShippingStreet("");
    setShippingCity("");
    setShippingZip("");
    setShippingState("");
    setShippingCountry("");
    setIsShippingValid(true);
    onOpen();
  }, [onOpen]);

  const onStartNewOrder = useCallback(() => {
    navigate("/customer/main");
  }, [navigate]);

  const modalMaxH = useMemo(() => {
    if (isMobile) return "100vh";

    if (processStep !== 0) return "80vh";

    return orderCountForGroup > 6 ? "80vh" : undefined;
  }, [isMobile, orderCountForGroup, processStep]);

  const selectedPageCount = Math.max(0, Number(selectedOrder?.page_count || 0) || 0);

  const effectivePrice = useMemo(() => {
    if (item.perPageUsd) {
      return item.perPageUsd * selectedPageCount;
    }
    return item.price;
  }, [item.perPageUsd, item.price, selectedPageCount]);

  const { onPayNow } = useServicePayment({
    item: { id: item.id, key: item.key, perPageUsd: item.perPageUsd, price: item.price },
    selectedOrder,
    cardInfo,
    isShippingValid,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingZip,
    shippingState,
    shippingCountry,
    saveCardAsDefault: uploadProject.saveCardAsDefault === true,
    userId: user.user_id,
    onClose,
    onRefresh,
    setIsProcessingPayment,
  });

  return (
    <>
      <ServiceCard
        title={item.title}
        description={item.description}
        priceLabel={item.perPageUsd ? `Add $${item.perPageUsd}` : `Add $${item.price}`}
        onAdd={onStartPaymentStep}
        {...rest}
      />
      <Modal isOpen={isOpen} onClose={onClose} size={modalSize}>
        <ModalOverlay />
        <ModalContent borderRadius={{ base: "0px", md: "12px" }}>
          <ModalHeader
            fontSize={{ base: "18px", md: "20px" }}
            lineHeight="24px"
            fontWeight="700"
            color="#000"
            textAlign="center"
            pt={{ base: "16px", md: "25px" }}
            position="relative"
          >
            {processStep === 1 ? (
              <Flex
                position="absolute"
                left={{ base: "12px", md: "24px" }}
                top={{ base: "12px", md: "22px" }}
                display={{ base: "flex", md: "none" }}
                alignItems="center"
                fontSize="14px"
                lineHeight="17px"
                color="#2F80ED"
                fontWeight="700"
                cursor="pointer"
                padding="10px"
                onClick={() => {
                  setProcessStep(0);
                }}
              >
                <IconBox
                  w="25px"
                  h="24px"
                  mr="10px"
                  icon={<IconArrowBack w="25px" h="24px" />}
                />
                Back
              </Flex>
            ) : null}
            {processStep === 0 && "Please select the Project"}
            {processStep === 1 && `Payment for : ${item.title}`}
          </ModalHeader>
          <ModalBody
            py={{ base: 3, md: 4 }}
            px={{ base: 3, md: 6 }}
            overflowY={{ base: "auto", md: modalMaxH ? "auto" : "visible" }}
          >
            {processStep === 0 && (
              <Box
                bg="#F5F7FA"
                border="1px solid"
                borderColor="#E5E7EB"
                borderRadius="16px"
                p={{ base: "12px", md: "0px" }}
              >
                <ProjectSelectTable
                  orders={eligibleOrders}
                  pagedOrders={pagedOrders}
                  selectedOrder={selectedOrder}
                  onSelectOrder={setSelectedOrder}
                  perPage={perPage}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChangePage={setCurrentPage}
                  onStartNewOrder={onStartNewOrder}
                />
              </Box>
            )}
            {processStep === 1 && (
              <PaymentStep
                selectedOrder={selectedOrder}
                onPickPaymentInfo={(e: any) => {
                  setCardInfo(e);
                }}
                showShippingDetails={item?.key === "include_mail_hard_copy"}
                perPageUsd={item.perPageUsd}
                pageCount={selectedPageCount}
                effectivePrice={effectivePrice}
                shippingRecipient={shippingRecipient}
                shippingStreet={shippingStreet}
                shippingCity={shippingCity}
                shippingZip={shippingZip}
                shippingState={shippingState}
                shippingCountry={shippingCountry}
                onChangeShippingRecipient={setShippingRecipient}
                onChangeShippingStreet={setShippingStreet}
                onChangeShippingCity={setShippingCity}
                onChangeShippingZip={setShippingZip}
                onChangeShippingState={setShippingState}
                onChangeShippingCountry={setShippingCountry}
                onShippingValidityChange={setIsShippingValid}
              />
            )}
          </ModalBody>
          <ModalFooter
            borderTop="1px solid #EEE"
            px={{ base: 3, md: 6 }}
            py={{ base: 3, md: 4 }}
            flexDirection={{ base: "column", md: "row" }}
            alignItems={{ base: "stretch", md: "center" }}
            gap={{ base: "12px", md: "0px" }}
          >
            <Button
              bgColor="#FFFFFF"
              color="#3A7BFF"
              h="48px"
              w={{ base: "100%", md: "230px" }}
              border="1px solid #3A7BFF"
              _hover={{
                bgColor: "#3A7BFF",
                color: "#FFF",
              }}
              _active={{
                bgColor: "#FFFFFF",
                color: "#3A7BFF",
              }}
              borderRadius="25px"
              fontSize="16px"
              lineHeight="17px"
              fontWeight="600"
              mr={{ base: 0, md: "auto" }}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            {processStep === 1 && (
              <Flex
                alignItems="center"
                fontSize="16px"
                lineHeight="17px"
                color="#2F80ED"
                fontWeight="700"
                cursor="pointer"
                padding="10px"
                mr={{ base: 0, md: "20px" }}
                justifyContent={{ base: "center", md: "flex-start" }}
                w={{ base: "100%", md: "auto" }}
                display={{ base: "none", md: "flex" }}
                onClick={() => {
                  setProcessStep(0);
                }}
              >
                <IconBox
                  w="25px"
                  h="24px"
                  mr="10px"
                  icon={<IconArrowBack w="25px" h="24px" />}
                />
                Back
              </Flex>
            )}
            {processStep === 0 && (
              <Button
                bgColor="#3A7BFF"
                color="white"
                h="48px"
                w={{ base: "100%", md: "230px" }}
                border={!selectedOrder ? "transparent" : "1px solid #3A7BFF"}
                _hover={{
                  bgColor: "#FFFFFF",
                  color: !selectedOrder ? "#FFFFFF" : "#3A7BFF",
                }}
                _active={{
                  bgColor: "#FFFFFF",
                  color: !selectedOrder ? "#9CA3AF" : "#3A7BFF",
                }}
                borderRadius="25px"
                fontSize="16px"
                lineHeight="17px"
                fontWeight="600"
                disabled={!selectedOrder}
                onClick={() => {
                  setProcessStep(1);
                }}
                _disabled={{
                  backgroundColor: "#E9E9E9 !important",
                }}
              >
                Confirm
              </Button>
            )}
            {processStep === 1 && (
              <Button
                bgColor="#3A7BFF"
                color="white"
                h="48px"
                w={{ base: "100%", md: "230px" }}
                border={!cardInfo ? "transparent" : "1px solid #3A7BFF"}
                _hover={{
                  bgColor: "#FFFFFF",
                  color: !cardInfo ? "#FFFFFF" : "#3A7BFF",
                }}
                _active={{
                  bgColor: "#FFFFFF",
                  color: !cardInfo ? "#9CA3AF" : "#3A7BFF",
                }}
                borderRadius="25px"
                fontSize="16px"
                lineHeight="17px"
                fontWeight="600"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                isLoading={isProcessingPayment}
                disabled={
                  !selectedOrder ||
                  !cardInfo ||
                  (item?.key === "include_mail_hard_copy" && !isShippingValid)
                }
                _disabled={{
                  backgroundColor: "#E9E9E9 !important",
                }}
                onClick={onPayNow}
              >
                {`Pay Now ($${effectivePrice.toFixed(2)})`}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
